import { useEffect, useState } from "react";
import { uploadFile, useFileUrl } from "../lib/files";
import { formatApiError } from "../lib/api";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";

const MAX_PHOTO_SIZE_BYTES = 50 * 1024 * 1024;
const ACCEPTED_PHOTO_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/pjpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
  "application/octet-stream",
]);

function photoValidationError(file) {
  if (file.size > MAX_PHOTO_SIZE_BYTES) {
    return "Photo is too large. Please upload a photo under 50MB; camera photos are resized automatically where possible.";
  }
  const type = (file.type || "").toLowerCase();
  if (!type) return null;
  if (ACCEPTED_PHOTO_TYPES.has(type) || type.startsWith("image/")) return null;
  return `Unsupported photo type (${type}). Please upload or capture a JPEG, JPG, HEIC, HEIF, PNG, or WEBP photo.`;
}


export default function PhotoUploader({ value, onChange, testId, label = "Photo", multiple = Array.isArray(value) }) {
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const values = Array.isArray(value) ? value : (value ? [value] : []);
  const hasPhotos = values.length > 0 || !!previewUrl;

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!values.length || !previewUrl) return;
    URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  }, [values.length, previewUrl]);

  const clearPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const validationError = files.map(photoValidationError).find(Boolean);
    if (validationError) {
      toast.error(validationError);
      e.target.value = "";
      return;
    }
    clearPreview();
    setPreviewUrl(URL.createObjectURL(files[0]));
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const r = await uploadFile(file);
        uploaded.push(r.path);
      }
      onChange(multiple ? [...values, ...uploaded] : uploaded[0]);
    } catch (err) {
      const reason = formatApiError(err.response?.data?.detail) || err.message || "Unknown upload error";
      toast.error(`Upload failed: ${reason}`);
      clearPreview();
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid={testId}>
      {label && (
        <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1">
          {label}
        </div>
      )}
      {hasPhotos ? (
        <div className="flex flex-wrap gap-2">
          {values.map((path, idx) => (
            <PhotoPreview
              key={path || idx}
              path={path}
              busy={busy}
              onRemove={() => {
                clearPreview();
                onChange(Array.isArray(value) ? value.filter((_, i) => i !== idx) : null);
              }}
              testId={`${testId}-remove-${idx}`}
            />
          ))}
          {!values.length && previewUrl && (
            <PhotoPreview previewUrl={previewUrl} busy={busy} onRemove={clearPreview} testId={`${testId}-remove-preview`} />
          )}
          <PhotoPicker busy={busy} onPick={onPick} testId={testId} multiple={multiple} />
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <PhotoPicker busy={busy} onPick={onPick} testId={testId} multiple={multiple} />
          <label
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-mono font-bold uppercase border border-gray-300 hover:border-black cursor-pointer"
          >
            <Upload size={12} />
            {busy ? "Uploading…" : "Take photo"}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={onPick}
              data-testid={`${testId}-camera-input`}
            />
          </label>
        </div>
      )}
    </div>
  );
}


function PhotoPreview({ path, previewUrl, busy, onRemove, testId }) {
  const fileUrl = useFileUrl(path || null);
  const displayUrl = previewUrl || fileUrl;
  if (!displayUrl) return null;
  return (
    <div className="relative inline-block">
      <a href={displayUrl} target="_blank" rel="noreferrer" title="Open full image">
        <img src={displayUrl} alt="Uploaded preview" className="max-h-40 border border-gray-300" />
      </a>
      {busy && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white text-[10px] font-mono uppercase tracking-wider">Uploading…</div>
      )}
      <button type="button" onClick={onRemove} className="absolute top-1 right-1 bg-black text-white p-1" data-testid={testId}>
        <X size={12} />
      </button>
    </div>
  );
}

function PhotoPicker({ busy, onPick, testId, multiple }) {
  return (
    <label className="inline-flex items-center gap-2 px-3 py-2 text-xs font-mono font-bold uppercase border border-gray-300 hover:border-black cursor-pointer">
      <Upload size={12} />
      {busy ? "Uploading…" : "Add photos"}
      <input type="file" accept="image/*,.jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden" onChange={onPick} multiple={multiple} data-testid={`${testId}-input`} />
    </label>
  );
}
